// Source unique des offres, prix et FAQ du site freelance.
// Utilisée par l'accueil, les pages services, la FAQ et le JSON-LD. À reporter aussi dans public/llms.txt.

export type Plan = {
    name: string;
    price: string;
    /** Prix minimum en euros, pour le JSON-LD (offre « à partir de »). */
    from?: number;
    period?: string;
    delay?: string;
    description: string;
    includes: string[];
    highlight?: boolean;
};

export type Service = {
    slug: 'sites-web' | 'application-web' | 'identite-visuelle';
    title: string;
    short: string;
    intro: string;
    priceHint: string;
    forWho: string[];
    plans: Plan[];
    steps: { title: string; text: string }[];
    faq: { q: string; a: string }[];
};

export const CONTACT = {
    email: 'bonjour@gauthier-rodriguez.fr',
    phone: '+33 6 30 26 78 33',
    phoneHref: 'tel:+33630267833',
    city: 'Toulouse',
    linkedin: 'https://www.linkedin.com/in/gauthier-rodriguez-b23253aa/',
};

export const services: Service[] = [
    {
        slug: 'sites-web',
        title: 'Sites web',
        short: 'Site vitrine, refonte et maintenance.',
        intro:
            'Je conçois et je code des sites vitrines rapides, faciles à lire sur mobile et bien référencés. Je refais aussi les sites vieillissants, et je peux m’occuper de la maintenance une fois le site en ligne.',
        priceHint: 'dès 1 500 €',
        forWho: [
            'Artisans et commerçants',
            'Professions libérales (avocats, médecins, architectes)',
            'Indépendants et petites entreprises',
            'Associations',
        ],
        plans: [
            {
                name: 'Site vitrine',
                price: 'dès 1 500 €',
                from: 1500,
                delay: '2 à 3 semaines',
                description: 'Un site clair de 5 à 7 pages pour présenter votre activité et recevoir des demandes.',
                includes: [
                    '5 à 7 pages (accueil, services, à propos, réalisations, contact)',
                    'Design sur mesure, adapté à votre identité',
                    'Formulaire de contact protégé contre le spam',
                    'Référencement Google de base',
                    'Affichage soigné sur mobile',
                    'Formation pour gérer votre site',
                ],
            },
            {
                name: 'Site vitrine premium',
                price: '2 500 – 4 000 €',
                from: 2500,
                delay: '3 à 4 semaines',
                description: 'Plus de pages, des animations et un design poussé pour se démarquer.',
                includes: [
                    'Tout le site vitrine',
                    'Design et animations plus travaillés',
                    'Pages supplémentaires',
                    'Référencement approfondi',
                ],
                highlight: true,
            },
            {
                name: 'Refonte',
                price: 'dès 2 000 €',
                from: 2000,
                description: 'Votre site est lent, daté ou invisible sur Google : je le refais sans perdre votre référencement.',
                includes: [
                    'Audit de l’existant offert',
                    'Reprise et amélioration de vos contenus',
                    'Redirections pour garder votre référencement',
                    'Nouveau design, rapide et adapté au mobile',
                    'Mise en ligne sans coupure',
                    'Un mois de support après la mise en ligne',
                ],
            },
            {
                name: 'Maintenance Essentiel',
                price: '79 €',
                from: 79,
                period: 'par mois',
                description: 'Pour un site vitrine avec un trafic modéré.',
                includes: [
                    'Mises à jour de sécurité',
                    'Sauvegardes hebdomadaires',
                    'Surveillance de disponibilité',
                    '1 h de modifications par mois',
                    'Réponse par email sous 48 h',
                ],
            },
            {
                name: 'Maintenance Pro',
                price: '149 €',
                from: 149,
                period: 'par mois',
                description: 'Pour un site plus fréquenté ou une application.',
                includes: [
                    'Tout le plan Essentiel',
                    'Sauvegardes quotidiennes',
                    'Alertes en cas de problème',
                    '3 h de modifications par mois',
                    'Réponse prioritaire sous 24 h',
                ],
            },
        ],
        steps: [
            { title: 'Échange', text: 'On parle de votre activité, de vos clients et de ce que le site doit leur faire faire.' },
            { title: 'Maquette', text: 'Je vous montre les pages avant de coder. On ajuste jusqu’à ce que ce soit juste.' },
            { title: 'Développement', text: 'Je code le site et vous le testez en ligne avant la mise en ligne.' },
            { title: 'Mise en ligne', text: 'Je connecte le nom de domaine, je vous forme et je reste disponible.' },
        ],
        faq: [
            {
                q: 'L’hébergement et le nom de domaine sont-ils inclus ?',
                a: 'Non, ce sont des frais à part : environ 15 € par an pour le nom de domaine et dès 5 € par mois pour l’hébergement. Je peux m’en occuper pour vous, et vous restez propriétaire de vos accès.',
            },
            {
                q: 'Pourrai-je modifier mon site moi-même ?',
                a: 'Oui si le site a une interface d’administration : je vous forme à l’utiliser. Pour les changements plus techniques, je m’en charge, ponctuellement ou avec la maintenance.',
            },
        ],
    },
    {
        slug: 'application-web',
        title: 'Applications web',
        short: 'Boutique, réservation, espace membre, outil métier.',
        intro:
            'Quand un site vitrine ne suffit plus, je développe l’outil dont vous avez besoin : vendre en ligne, prendre des rendez-vous, gérer vos clients ou ouvrir un espace réservé à vos membres.',
        priceHint: 'dès 3 000 €',
        forWho: [
            'Boutiques en ligne sur mesure',
            'Réservation et prise de rendez-vous',
            'Espaces membres',
            'Outils de gestion (clients, devis, planning, stock)',
            'Sites de contenu avec administration',
        ],
        plans: [
            {
                name: 'Application web',
                price: 'dès 3 000 €',
                from: 3000,
                delay: '4 à 10 semaines selon le projet',
                description: 'Le prix dépend des fonctionnalités : je vous envoie un devis détaillé après notre échange.',
                includes: [
                    'Maquettes validées avant le développement',
                    'Base de données et interface d’administration',
                    'Paiement, emails automatiques, connexion des utilisateurs selon le besoin',
                    'Version de test en ligne pendant le développement',
                    'Conformité RGPD',
                    'Formation et documentation',
                ],
                highlight: true,
            },
        ],
        steps: [
            { title: 'Cadrage', text: 'On liste ce que l’outil doit faire et pour qui. J’en tire un cahier des charges et un devis.' },
            { title: 'Maquettes', text: 'Je dessine les écrans principaux pour valider le parcours avant d’écrire une ligne de code.' },
            { title: 'Développement', text: 'Je développe par étapes, avec une version de test que vous pouvez essayer.' },
            { title: 'Mise en ligne', text: 'Je déploie l’application, je vous forme et je reste disponible pour la faire évoluer.' },
        ],
        faq: [
            {
                q: 'Avec quelles technologies travaillez-vous ?',
                a: 'React, Next.js et TypeScript, avec une base de données adaptée au projet. L’application est hébergée chez un hébergeur reconnu, avec un certificat SSL.',
            },
            {
                q: 'Pourra-t-on ajouter des fonctionnalités plus tard ?',
                a: 'Oui. Le code est organisé et documenté pour pouvoir évoluer, avec moi ou avec un autre développeur.',
            },
        ],
    },
    {
        slug: 'identite-visuelle',
        title: 'Identité visuelle',
        short: 'Logo, carte de visite, identité complète.',
        intro:
            'Je dessine des logos et des identités qui vous ressemblent, avec tous les fichiers pour les utiliser partout : site, réseaux sociaux, impression, enseigne.',
        priceHint: 'dès 250 €',
        forWho: ['Création d’entreprise', 'Changement d’image', 'Lancement d’un produit ou d’une gamme'],
        plans: [
            {
                name: 'Carte de visite',
                price: 'dès 250 €',
                from: 250,
                description: 'Recto verso, prête à imprimer.',
                includes: ['Création recto verso', 'Fichiers prêts pour l’imprimeur', 'Conseils papier et finition'],
            },
            {
                name: 'Logo',
                price: 'dès 500 €',
                from: 500,
                delay: '7 à 10 jours',
                description: 'Plusieurs pistes, puis un logo finalisé avec ses déclinaisons.',
                includes: [
                    '2 à 3 pistes créatives',
                    'Version principale, simplifiée et monochrome',
                    'Fichiers vectoriels (SVG, AI) et PNG haute définition',
                    'Vous êtes propriétaire de votre logo',
                ],
            },
            {
                name: 'Identité complète',
                price: 'dès 1 200 €',
                from: 1200,
                delay: '2 à 4 semaines',
                description: 'Logo, couleurs, typographies et supports, réunis dans une charte.',
                includes: [
                    'Logo et déclinaisons',
                    'Palette de couleurs (codes web et impression)',
                    'Typographies et hiérarchie des textes',
                    'Carte de visite, papier à en-tête, signature email',
                    'Charte graphique en PDF',
                ],
                highlight: true,
            },
        ],
        steps: [
            { title: 'Brief', text: 'Je pose des questions sur votre activité, vos clients et l’image que vous voulez donner.' },
            { title: 'Recherches', text: 'Concurrence, références, typographies et couleurs.' },
            { title: 'Pistes', text: 'Je vous présente 2 à 3 directions différentes, puis on affine celle que vous choisissez.' },
            { title: 'Livraison', text: 'Vous recevez tous les fichiers sources et la charte pour bien les utiliser.' },
        ],
        faq: [
            {
                q: 'Et si aucune piste ne me plaît ?',
                a: 'Je repars sur de nouvelles pistes, sans frais supplémentaires.',
            },
            {
                q: 'Je reçois les fichiers sources ?',
                a: 'Oui, tous : vous êtes propriétaire de votre identité visuelle.',
            },
        ],
    },
];

/** Questions générales, affichées sur l'accueil. */
export const faq = [
    {
        q: 'Combien de temps faut-il pour créer un site ?',
        a: 'Comptez 2 à 3 semaines pour un site vitrine et 4 à 10 semaines pour une application, selon le projet. Je vous donne un planning dès que le devis est validé.',
    },
    {
        q: 'Le site sera-t-il adapté au mobile ?',
        a: 'Oui, je conçois chaque site d’abord pour le mobile, puis pour la tablette et l’ordinateur.',
    },
    {
        q: 'Mon site sera-t-il conforme au RGPD ?',
        a: 'Oui : bannière cookies, politique de confidentialité, formulaires avec consentement et hébergement en Europe.',
    },
    {
        q: 'Et si le résultat ne me convient pas ?',
        a: 'On valide chaque étape ensemble (maquette, version de test) avant d’avancer. Si quelque chose ne vous va pas, je le reprends.',
    },
    {
        q: 'Pourquoi pas Wix ou WordPress.com ?',
        a: 'Avec un site sur mesure, vous êtes propriétaire du code, le site est plus rapide et mieux référencé, et vous ne dépendez pas de l’abonnement d’une plateforme.',
    },
    ...services.flatMap((s) => s.faq.slice(0, 1)),
];

export const serviceBySlug = (slug: Service['slug']) => services.find((s) => s.slug === slug)!;
